/**
 * Material Preference Coordinator - Phase 2 Runtime Module
 *
 * PURPOSE: Coordinates material preference application and cascading category updates
 * PHASE: 2 (Event-Driven Runtime - runs AFTER bootstrap completion)
 * CCP STATUS: STRICT ENFORCEMENT - All data via PPD, all communication via events
 * GOLDEN STANDARD REFERENCE: md-config-bl-material-selector.js (200/200)
 * TARGET COMPLIANCE SCORE: 190+/200 (EXEMPLARY)
 *
 * CCP COMPLIANCE:
 * - CONSTANTS.EVENTS.* exclusively (no local EVENT_TYPES - Patterns 1, 19)
 * - publishEvent() async helper with try/catch (Patterns 2, 11)
 * - 5-stage event lifecycle: STARTED -> VALIDATION -> PROGRESS -> COMPLETED/FAILED (Pattern 3)
 * - Event consumer handlers for bootstrap and runtime events (Pattern 4)
 * - Event replay ready via SYSTEM_STATE_FROM_PPD_READY subscription (Pattern 5)
 * - Bootstrap runtime protection via bootstrapComplete guard (Pattern 6)
 * - PPD-first O(1) data access via conductor delegation (Patterns 7, 8)
 * - Data access through conductor delegation (Pattern 9)
 * - UI updates through events (Pattern 10)
 * - Async event publishing (Pattern 11)
 * - Performance tracking with thresholds (Patterns 12, 13)
 * - Try/catch + FAILED events on all operations (Pattern 14)
 * - Cleanup arrays for all subscriptions (Pattern 15)
 * - Initialization guard (Pattern 16)
 * - No user input rendered to DOM (Pattern 17 - N/A for pure BL)
 * - CoreEventBus central freeze (Pattern 18 DRY)
 * - CONSTANTS.EVENTS.* usage throughout (Pattern 19)
 * - Conductor single publication (Pattern 20)
 *
 * RESPONSIBILITIES:
 * 1. Apply material preferences (wood/finish) and update state
 * 2. Publish cascading events for category UI updates
 * 3. Provide O(1) filtered/sorted variant lookups from PPD
 * 4. Determine if products have compatible variants for current preferences
 * 5. Coordinate bidirectional material preference propagation
 *
 * ARCHITECTURE:
 * - Event-driven: Publishes material:preference_applied for cascading updates
 * - PPD-first: All data lookups are O(1) Map.get() from pre-computed PPD structures
 * - State-managed: Updates go through state-manager
 * - Zero iteration: No runtime filtering or sorting (uses bootstrap pre-computed maps)
 * - Pre-computed filter states: materialPreferenceToFilterStateMap eliminates O(C*P*V) loops
 * - Centralized init: Conductor dependency checks run once at init, not per-method
 *
 * EVENT FLOW:
 *
 * User applies material:
 * 1. User selects variant with wood/finish in ANY category
 * 2. bl-grouped-product-handler detects material change
 * 3. Calls materialPreferenceCoordinator.applyMaterialPreference()
 * 4. Coordinator updates state-manager with new preference
 * 5. Coordinator publishes material:preference_applied event
 * 6. ui-navigation-manager subscribes, detects affected categories
 * 7. ui-updater re-renders categories with filtered/sorted variants
 * 8. UI shows simplified (filtered) or full (sorted) view based on config
 *
 * INTEGRATION POINTS:
 * - Called by: md-config-bl-grouped-product-handler.js when variant applied
 * - Uses PPD: materialFilteredVariantMaps, materialSortedVariantOrders (from filter-builder)
 * - Updates: md-config-core-state-manager.js materialPreferences
 * - Publishes to: md-config-core-event-bus.js
 * - Consumed by: md-config-ui-navigation-manager.js, md-config-ui-updater.js
 *
 * PERFORMANCE:
 * - applyMaterialPreference(): O(1) state update + O(1) event publish
 * - getFilteredVariants(): O(1) Map.get() lookup from PPD
 * - getSortedVariants(): O(1) cache hit after first access per product; O(W*F) lazy compute on first access
 * - hasCompatibleVariants(): O(1) Map.get() + array length check
 * - computeAndPublishFilterState(): O(1) cache hit; O(C*P*V) lazy compute on first access per preference key
 */

/** Module uses scoped pattern for Shopify compatibility */
(function (window) {
  'use strict';

  window.MurphyDoorConfigurator = window.MurphyDoorConfigurator || {};

  // ============================================================================
  // PATTERN 1 & 19: GLOBAL CONSTANTS USAGE (10 points each)
  // All events use CONSTANTS.EVENTS.* - NO local EVENT_TYPES objects (DRY)
  // ============================================================================
  const CONSTANTS = window.MurphyDoorConfigurator.CONSTANTS;
  const EVENTS = CONSTANTS.EVENTS;
  const CoreEventBus = window.MurphyDoorConfigurator.CoreEventBus;

  // Dev-only logging (production guard)
  const isDev = () =>
    window.MurphyDoorConfigurator?.utils?.isDevelopment?.() ?? false;

  const MODULE_NAME = 'MaterialPreferenceCoordinator';
  const LOG_PREFIX = `[${MODULE_NAME}]`;

  // ============================================================================
  // PATTERN 13: PERFORMANCE THRESHOLDS (10 points)
  // ============================================================================
  const PERFORMANCE_THRESHOLDS = Object.freeze({
    APPLY_PREFERENCE: 50, // ms - state update + event publish
    CLEAR_PREFERENCE: 50, // ms - state update + event publish
    FILTER_STATE_COMPUTE: 50, // ms - O(1) PPD lookup or lazy fallback
    FILTERED_VARIANTS: 15, // ms - O(1) Map.get()
    SORTED_VARIANTS: 15, // ms - O(1) Map.get() or lazy compute
  });

  // ============================================================================
  // PATTERN 16: INITIALIZATION GUARD (10 points)
  // ============================================================================
  let isInitialized = false;

  // ============================================================================
  // PATTERN 6: BOOTSTRAP RUNTIME PROTECTION (10 points)
  // ============================================================================
  let bootstrapComplete = false;

  // ============================================================================
  // PATTERN 15: CLEANUP ARRAYS (10 points)
  // ============================================================================
  const cleanupHandlers = [];

  // Runtime lazy caches - populated on first access per product/preference key
  // These replace bootstrap pre-computation of sorted orders and filter state maps
  const lazySortedOrdersCache = new Map();
  const lazyFilterStateCache = new Map();

  // ============================================================================
  // PATTERN 2: publishEvent() ASYNC HELPER (10 points)
  // PATTERN 11: ASYNC EVENT PUBLISHING (10 points)
  // NOTE: Object.freeze() NOT needed - CoreEventBus.publish() freezes centrally (Pattern 18 DRY)
  // ============================================================================
  async function publishEvent(eventType, eventData) {
    try {
      if (CoreEventBus?.publish) {
        await CoreEventBus.publish(eventType, eventData);
      } else {
        isDev() &&
          console.warn(
            `${LOG_PREFIX} Event bus unavailable, failed to publish: ${eventType}`
          );
      }
    } catch (error) {
      isDev() &&
        console.error(`${LOG_PREFIX} Error publishing ${eventType}:`, error);
    }
  }

  // ============================================================================
  // PATTERN 12: PERFORMANCE TRACKING (10 points)
  // ============================================================================
  function trackPerformance(operationType, duration) {
    const threshold = PERFORMANCE_THRESHOLDS[operationType];
    if (threshold && duration > threshold) {
      publishEvent(EVENTS.PERFORMANCE_MATERIAL_PREFERENCE_SLOW, {
        operation: operationType,
        duration,
        threshold,
        severity: duration > threshold * 2 ? 'critical' : 'warning',
        module: MODULE_NAME,
        timestamp: Date.now(),
      });
    }
  }

  // ============================================================================
  // PATTERN 4: EVENT CONSUMER HANDLERS (10 points)
  // PATTERN 5: EVENT REPLAY READY (10 points)
  // PATTERN 6: BOOTSTRAP SUBSCRIPTION AT MODULE LOAD TIME (10 points)
  // ============================================================================
  if (CoreEventBus) {
    // Subscribe to bootstrap completion at module load time (race protection)
    const unsubBootstrap = CoreEventBus.subscribe(
      EVENTS.SYSTEM_STATE_FROM_PPD_READY,
      () => {
        isDev() &&
          console.log(`${LOG_PREFIX} Bootstrap complete event received`);
        bootstrapComplete = true;
      }
    );
    cleanupHandlers.push(unsubBootstrap);

    isDev() &&
      console.log(
        `${LOG_PREFIX} Subscribed to bootstrap event at module load time`
      );
  }

  // ============================================================================
  // MODULE IMPLEMENTATION
  // ============================================================================
  window.MurphyDoorConfigurator.materialPreferenceCoordinator = {
    /**
     * Apply material preference and trigger cascading updates
     *
     * Called when user selects a variant with wood/finish in ANY category.
     * Updates state-manager and publishes event for cascading category updates.
     *
     * Implements 5-stage event lifecycle:
     * STARTED -> VALIDATION -> PROGRESS -> COMPLETED/FAILED
     *
     * @param {String} materialType - 'wood' or 'finish'
     * @param {String} materialSku - Material SKU (e.g., 'PG', 'UN', 'MWH')
     * @param {Object} context - Optional context (productId, variantId, categoryId)
     * @returns {Object} Result with success status
     */
    applyMaterialPreference: function (
      materialType,
      materialSku,
      context = {}
    ) {
      const METHOD_SIG = `${MODULE_NAME}.applyMaterialPreference`;
      const startTime = performance.now();

      // PATTERN 6: Bootstrap runtime protection
      if (!bootstrapComplete) {
        isDev() &&
          console.warn(`${LOG_PREFIX} Called before bootstrap complete`);
        return { success: false, error: 'Not bootstrapped' };
      }

      try {
        // ================================================================
        // STAGE 1: START - Announce operation beginning
        // ================================================================
        publishEvent(EVENTS.MATERIAL_PREFERENCE_APPLY_STARTED, {
          materialType,
          materialSku,
          context,
          timestamp: Date.now(),
          source: METHOD_SIG,
        });

        // ================================================================
        // STAGE 2: VALIDATION - Validate inputs
        // ================================================================
        if (
          materialType !== CONSTANTS.MATERIAL_TYPES.WOOD &&
          materialType !== CONSTANTS.MATERIAL_TYPES.FINISH
        ) {
          const error = `Invalid materialType: ${materialType}. Must be '${CONSTANTS.MATERIAL_TYPES.WOOD}' or '${CONSTANTS.MATERIAL_TYPES.FINISH}'.`;

          publishEvent(EVENTS.MATERIAL_PREFERENCE_APPLY_VALIDATION, {
            valid: false,
            error,
            materialType,
            timestamp: Date.now(),
            source: METHOD_SIG,
          });

          throw new Error(error);
        }

        publishEvent(EVENTS.MATERIAL_PREFERENCE_APPLY_VALIDATION, {
          valid: true,
          materialType,
          materialSku,
          timestamp: Date.now(),
          source: METHOD_SIG,
        });

        // ================================================================
        // STAGE 3: PROGRESS - Business logic execution
        // ================================================================
        // PATTERN 9: Data access through conductor delegation
        const conductor = window.MurphyDoorConfigurator.conductor;
        const currentPrefs = conductor.getMaterialPreferences() || {
          woodApplied: false,
          finishApplied: false,
          appliedWoodSku: null,
          appliedFinishSku: null,
          lastUpdated: null,
        };

        // Guard against infinite recursion - skip state update if preference is already applied
        const materialTypeCapitalized =
          materialType.charAt(0).toUpperCase() + materialType.slice(1);
        if (
          currentPrefs[`applied${materialTypeCapitalized}Sku`] === materialSku
        ) {
          isDev() &&
            console.log(
              `${LOG_PREFIX} ${materialType} preference '${materialSku}' already applied. Re-computing filter state for UI consistency.`
            );

          // Even though preference hasn't changed, re-compute and publish filter state
          // This ensures UI updates when navigating to different categories with same preferences
          this.computeAndPublishFilterState();

          publishEvent(EVENTS.MATERIAL_PREFERENCE_APPLY_COMPLETED, {
            materialType,
            materialSku,
            preferences: currentPrefs,
            noChange: true,
            duration: performance.now() - startTime,
            timestamp: Date.now(),
            source: METHOD_SIG,
          });

          return { success: true, preferences: currentPrefs, noChange: true };
        }

        publishEvent(EVENTS.MATERIAL_PREFERENCE_APPLY_PROGRESS, {
          stage: 'updating_state',
          materialType,
          materialSku,
          timestamp: Date.now(),
          source: METHOD_SIG,
        });

        // Build updated preferences object
        const updatedPrefs = {
          ...currentPrefs,
          [`${materialType}Applied`]: true,
          [`applied${materialTypeCapitalized}Sku`]: materialSku,
          lastUpdated: Date.now(),
        };

        // Update state via conductor delegation (CCP compliant)
        conductor.updateMaterialPreferencesFromObject(updatedPrefs);

        // Publish domain event for cascading updates
        publishEvent(EVENTS.MATERIAL_PREFERENCE_APPLIED, {
          materialType,
          materialSku,
          preferences: updatedPrefs,
          context,
          timestamp: Date.now(),
          source: METHOD_SIG,
        });

        // Compute and publish filter state for simplified UX
        this.computeAndPublishFilterState();

        // ================================================================
        // STAGE 4: COMPLETED - Operation succeeded
        // ================================================================
        const duration = performance.now() - startTime;

        publishEvent(EVENTS.MATERIAL_PREFERENCE_APPLY_COMPLETED, {
          materialType,
          materialSku,
          preferences: updatedPrefs,
          duration,
          timestamp: Date.now(),
          source: METHOD_SIG,
        });

        // PATTERN 12: Performance tracking
        trackPerformance('APPLY_PREFERENCE', duration);

        isDev() &&
          console.log(`${LOG_PREFIX} Applied ${materialType} preference`, {
            materialSku,
            preferences: updatedPrefs,
            context,
            duration: duration.toFixed(2) + 'ms',
          });

        return { success: true, preferences: updatedPrefs };
      } catch (error) {
        // ================================================================
        // STAGE 5: FAILED - Operation failed
        // ================================================================
        const duration = performance.now() - startTime;

        // PATTERN 14: Try/catch + FAILED events
        publishEvent(EVENTS.MATERIAL_PREFERENCE_APPLY_FAILED, {
          error: error.message,
          materialType,
          materialSku,
          context,
          duration,
          timestamp: Date.now(),
          source: METHOD_SIG,
        });

        isDev() &&
          console.error(
            `${LOG_PREFIX} Error applying material preference`,
            error
          );
        throw error;
      }
    },

    /**
     * Clear material preference (both or specific type)
     *
     * @param {String|null} materialType - 'wood', 'finish', or null (clear both)
     * @returns {Object} Result with success status
     */
    clearMaterialPreference: function (materialType = null) {
      const METHOD_SIG = `${MODULE_NAME}.clearMaterialPreference`;
      const startTime = performance.now();

      // PATTERN 6: Bootstrap runtime protection
      if (!bootstrapComplete) {
        isDev() &&
          console.warn(`${LOG_PREFIX} Called before bootstrap complete`);
        return { success: false, error: 'Not bootstrapped' };
      }

      try {
        // PATTERN 9: Data access through conductor delegation
        const conductor = window.MurphyDoorConfigurator.conductor;
        const currentPrefs = conductor.getMaterialPreferences() || {
          woodApplied: false,
          finishApplied: false,
          appliedWoodSku: null,
          appliedFinishSku: null,
          lastUpdated: null,
        };

        // Build cleared preferences based on materialType
        let updatedPrefs;
        const now = Date.now();

        if (materialType === null) {
          // Clear both wood and finish
          updatedPrefs = {
            ...currentPrefs,
            woodApplied: false,
            finishApplied: false,
            appliedWoodSku: null,
            appliedFinishSku: null,
            lastUpdated: now,
          };
        } else if (materialType === CONSTANTS.MATERIAL_TYPES.WOOD) {
          // Clear only wood
          updatedPrefs = {
            ...currentPrefs,
            woodApplied: false,
            appliedWoodSku: null,
            lastUpdated: now,
          };
        } else if (materialType === CONSTANTS.MATERIAL_TYPES.FINISH) {
          // Clear only finish
          updatedPrefs = {
            ...currentPrefs,
            finishApplied: false,
            appliedFinishSku: null,
            lastUpdated: now,
          };
        } else {
          throw new Error(
            `Invalid materialType: ${materialType}. Must be '${CONSTANTS.MATERIAL_TYPES.WOOD}', '${CONSTANTS.MATERIAL_TYPES.FINISH}', or null.`
          );
        }

        // Update state via conductor delegation (CCP compliant)
        conductor.updateMaterialPreferencesFromObject(updatedPrefs);

        // Publish domain event
        publishEvent(EVENTS.MATERIAL_PREFERENCE_CLEARED, {
          materialType,
          preferences: updatedPrefs,
          timestamp: now,
          source: METHOD_SIG,
        });

        // Compute and publish filter state for simplified UX
        this.computeAndPublishFilterState();

        const duration = performance.now() - startTime;

        // PATTERN 12: Performance tracking
        trackPerformance('CLEAR_PREFERENCE', duration);

        isDev() &&
          console.log(
            `${LOG_PREFIX} Cleared ${materialType || 'all'} material preferences`,
            updatedPrefs
          );

        return { success: true, preferences: updatedPrefs };
      } catch (error) {
        // PATTERN 14: Try/catch + FAILED events
        const duration = performance.now() - startTime;

        publishEvent(EVENTS.MATERIAL_PREFERENCE_APPLY_FAILED, {
          error: error.message,
          operation: 'clear',
          materialType,
          duration,
          timestamp: Date.now(),
          source: METHOD_SIG,
        });

        isDev() &&
          console.error(
            `${LOG_PREFIX} Error clearing material preference`,
            error
          );
        throw error;
      }
    },

    /**
     * Get filtered variants for a product based on current material preferences
     *
     * PERFORMANCE: O(1) - Direct Map.get() lookup from PPD materialFilteredVariantMaps
     *
     * Returns pre-filtered variant array based on applied wood/finish:
     * - If both wood + finish applied: Returns exact match variants (from byCombination map)
     * - If only wood applied: Returns all variants with that wood (from byWood map)
     * - If only finish applied: Returns all variants with that finish (from byFinish map)
     * - If nothing applied: Returns all variants (from allVariants)
     *
     * @param {String} productId - Product ID to get variants for
     * @param {Object|null} preferences - Material preferences (defaults to current state)
     * @returns {Array|null} Filtered variants array, or null if product not found
     */
    getFilteredVariants: function (productId, preferences = null) {
      const METHOD_SIG = `${MODULE_NAME}.getFilteredVariants`;

      try {
        // PATTERN 9: Data access through conductor delegation
        const conductor = window.MurphyDoorConfigurator.conductor;
        const prefs = preferences || conductor.getMaterialPreferences() || {};

        // PATTERN 7: PPD-first O(1) data access
        const ppd = conductor.getPerfectProductDictionary();
        const productFilterMap =
          ppd.materialFilteredVariantMaps?.get(productId);

        if (!productFilterMap) {
          isDev() &&
            console.warn(
              `${LOG_PREFIX} No filter map found for product ${productId}`
            );
          return null;
        }

        // Determine which filter to use based on preferences
        const hasWood = prefs?.woodApplied && prefs.appliedWoodSku;
        const hasFinish = prefs?.finishApplied && prefs.appliedFinishSku;

        // Extract material-agnostic variants (always compatible with any material preference)
        // Material-agnostic variants have NO wood_sku AND NO finish_sku (e.g., "No Back Casing")
        // These should ALWAYS appear regardless of applied material preferences
        const materialAgnosticVariants = productFilterMap.allVariants.filter(
          (variant) => !variant.wood_sku && !variant.finish_sku
        );

        // Return appropriate pre-filtered array (O(1) Map.get()) + material-agnostic variants
        let filteredVariants = [];
        if (hasWood && hasFinish) {
          // Both wood and finish applied - exact match + material-agnostic
          const comboKey = `${prefs.appliedWoodSku}|${prefs.appliedFinishSku}`;
          filteredVariants = productFilterMap.byCombination.get(comboKey) || [];
        } else if (hasWood) {
          // Only wood applied + material-agnostic
          filteredVariants =
            productFilterMap.byWood.get(prefs.appliedWoodSku) || [];
        } else if (hasFinish) {
          // Only finish applied + material-agnostic
          filteredVariants =
            productFilterMap.byFinish.get(prefs.appliedFinishSku) || [];
        } else {
          // No preferences applied - return all variants
          return productFilterMap.allVariants;
        }

        // Combine material-specific filtered variants with material-agnostic variants
        // Material-agnostic variants are ALWAYS compatible regardless of applied preferences
        return [...filteredVariants, ...materialAgnosticVariants];
      } catch (error) {
        isDev() &&
          console.error(`${LOG_PREFIX} Error getting filtered variants`, error);
        throw error;
      }
    },

    /**
     * Get sorted variants for a product based on current material preferences
     *
     * PERFORMANCE: O(1) - Direct Map.get() lookup from PPD materialSortedVariantOrders
     *
     * @param {String} productId - Product ID to get variants for
     * @param {Object|null} preferences - Material preferences (defaults to current state)
     * @returns {Array|null} Sorted variants array, or null if product not found
     */
    getSortedVariants: function (productId, preferences = null) {
      const METHOD_SIG = `${MODULE_NAME}.getSortedVariants`;

      try {
        // PATTERN 9: Data access through conductor delegation
        const conductor = window.MurphyDoorConfigurator.conductor;
        const prefs = preferences || conductor.getMaterialPreferences() || {};

        // PATTERN 7: PPD-first O(1) data access
        const ppd = conductor.getPerfectProductDictionary();
        let productSortOrders = ppd.materialSortedVariantOrders?.get(productId);

        // If not pre-computed, lazily compute via conductor delegation (deferred from bootstrap)
        if (!productSortOrders) {
          productSortOrders = this._getLazySortedOrders(productId);
          if (!productSortOrders) {
            isDev() &&
              console.warn(
                `${LOG_PREFIX} No sort orders available for product ${productId}`
              );
            return null;
          }
        }

        // Determine which sorted order to use based on preferences
        const hasWood = prefs?.woodApplied && prefs.appliedWoodSku;
        const hasFinish = prefs?.finishApplied && prefs.appliedFinishSku;

        // Return appropriate pre-sorted array (O(1) Map.get())
        if (hasWood && hasFinish) {
          const comboKey = `${prefs.appliedWoodSku}|${prefs.appliedFinishSku}`;
          return (
            productSortOrders.byCombinationPriority.get(comboKey) ||
            productSortOrders.defaultOrder
          );
        } else if (hasWood) {
          return (
            productSortOrders.byWoodPriority.get(prefs.appliedWoodSku) ||
            productSortOrders.defaultOrder
          );
        } else if (hasFinish) {
          return (
            productSortOrders.byFinishPriority.get(prefs.appliedFinishSku) ||
            productSortOrders.defaultOrder
          );
        }

        return productSortOrders.defaultOrder;
      } catch (error) {
        isDev() &&
          console.error(`${LOG_PREFIX} Error getting sorted variants`, error);
        throw error;
      }
    },

    /**
     * Check if product has compatible variants for current material preferences
     *
     * PERFORMANCE: O(1) - Uses getFilteredVariants() which is O(1) Map lookup
     *
     * @param {String} productId - Product ID to check
     * @param {Object|null} preferences - Material preferences (defaults to current state)
     * @returns {Boolean} True if product has compatible variants
     */
    hasCompatibleVariants: function (productId, preferences = null) {
      try {
        const filteredVariants = this.getFilteredVariants(
          productId,
          preferences
        );
        return filteredVariants && filteredVariants.length > 0;
      } catch (error) {
        isDev() &&
          console.error(`${LOG_PREFIX} Error checking compatibility`, error);
        return false;
      }
    },

    /**
     * Get simplified UX config from PPD
     *
     * @returns {Object} Simplified UX configuration
     */
    getSimplifiedUxConfig: function () {
      try {
        // PATTERN 9: Data access through conductor delegation
        const conductor = window.MurphyDoorConfigurator.conductor;
        const ppd = conductor.getPerfectProductDictionary();
        return ppd.simplifiedUxConfig || { enabled: true }; // Default fallback
      } catch (error) {
        isDev() &&
          console.error(
            `${LOG_PREFIX} Error getting simplified UX config`,
            error
          );
        return { enabled: true }; // Safe fallback
      }
    },

    /**
     * Helper: Get all variants for a product (fallback)
     *
     * @param {String} productId - Product ID
     * @returns {Array|null} All variants
     * @private
     */
    _getAllVariantsForProduct: function (productId) {
      try {
        // PATTERN 7 & 9: PPD-first via conductor delegation
        const conductor = window.MurphyDoorConfigurator.conductor;
        const ppd = conductor.getPerfectProductDictionary();
        const product = ppd.products?.get(productId);
        return product?.variants || null;
      } catch (error) {
        isDev() &&
          console.error(`${LOG_PREFIX} Error getting all variants`, error);
        return null;
      }
    },

    /**
     * Compute and publish filter state for simplified UX
     *
     * PERFORMANCE: O(1) - Uses pre-computed materialPreferenceToFilterStateMap from PPD.
     * Constructs a preference key and does Map.get() instead of O(C*P*V) nested loops.
     *
     * Called whenever material preferences change to broadcast filter state to UI modules.
     * Determines which variants are compatible/incompatible for each category.
     */
    computeAndPublishFilterState: function () {
      const METHOD_SIG = `${MODULE_NAME}.computeAndPublishFilterState`;
      const startTime = performance.now();

      try {
        // PATTERN 9: Data access through conductor delegation
        const conductor = window.MurphyDoorConfigurator.conductor;
        const preferences = conductor.getMaterialPreferences() || {};
        const simplifiedUxConfig = this.getSimplifiedUxConfig();

        // Check if simplified UX is enabled or if no preferences are applied
        const hasWood = preferences.woodApplied && preferences.appliedWoodSku;
        const hasFinish =
          preferences.finishApplied && preferences.appliedFinishSku;
        const noPreferencesApplied = !hasWood && !hasFinish;

        if (!simplifiedUxConfig.enabled || noPreferencesApplied) {
          isDev() &&
            console.log(
              `${LOG_PREFIX} Simplified UX disabled or no preferences applied, skipping filter computation`
            );
          // Clear any stale filter state so UI shows all variants unfiltered after unapply
          if (noPreferencesApplied) {
            conductor.updateMaterialFilterState(new Map());
          }
          return;
        }

        // Construct preference key for O(1) lookup from pre-computed PPD map
        // Uses centralized utility (single source of truth for key format)
        const preferenceKey =
          window.MurphyDoorConfigurator.utils.buildMaterialPreferenceKey(
            preferences
          );

        // PATTERN 7: PPD-first O(1) data access
        const ppd = conductor.getPerfectProductDictionary();
        if (!ppd) {
          isDev() && console.warn(`${LOG_PREFIX} PPD not available`);
          return;
        }

        const filterStateMap = ppd.materialPreferenceToFilterStateMap;
        let categoryFilters;

        if (filterStateMap && filterStateMap.has(preferenceKey)) {
          // O(1) lookup - pre-computed during bootstrap
          categoryFilters = filterStateMap.get(preferenceKey);
        } else {
          // Fallback: Compute at runtime for unexpected preference combinations, then cache
          // This handles edge cases where a combo wasn't pre-computed (e.g., wood+finish
          // combo where no variant has both SKUs, but user applied them independently)
          if (lazyFilterStateCache.has(preferenceKey)) {
            categoryFilters = lazyFilterStateCache.get(preferenceKey);
          } else {
            isDev() &&
              console.warn(
                `${LOG_PREFIX} No pre-computed filter state for key '${preferenceKey}', computing at runtime`
              );
            categoryFilters = this._computeFilterStateFallback(
              preferences,
              ppd
            );
            lazyFilterStateCache.set(preferenceKey, categoryFilters);
          }
        }

        // Store filter state in state manager via conductor delegation
        conductor.updateMaterialFilterState(categoryFilters);

        // Detect bubble-up variants across all categories for quick UI check
        // Bubble-up occurs when a multi-variant product reduces to exactly 1 compatible variant.
        // The UI uses this flag to decide whether to morph product thumbnails into variant thumbnails.
        let hasBubbleUpVariants = false;
        let bubbleUpCount = 0;
        categoryFilters.forEach(function (filterState) {
          if (filterState.bubbleUpVariants) {
            hasBubbleUpVariants = true;
            bubbleUpCount += filterState.bubbleUpVariants.size;
          }
        });

        // PATTERN 10: UI update events
        publishEvent(EVENTS.MATERIAL_FILTER_STATE_UPDATED, {
          categoryFilters,
          preferences,
          simplifiedUxEnabled: simplifiedUxConfig.enabled,
          hasBubbleUpVariants: hasBubbleUpVariants,
          timestamp: Date.now(),
          source: METHOD_SIG,
        });

        isDev() &&
          console.log(
            `${LOG_PREFIX} Published filter state for ${categoryFilters.size} categories (key: ${preferenceKey})` +
              (hasBubbleUpVariants
                ? ` [BUBBLE-UP: ${bubbleUpCount} variants across categories]`
                : '')
          );

        // Publish auto-apply and all-greyed events per category (event-driven, no DOM manipulation)
        categoryFilters.forEach((filterState, categoryId) => {
          // Auto-apply: When exactly one compatible variant remains, request automatic selection
          if (filterState.autoApplyVariantId) {
            publishEvent(EVENTS.SELECTION_AUTO_APPLY_REQUEST, {
              variantId: filterState.autoApplyVariantId,
              categoryId: categoryId,
              reason: 'simplified_ux_single_compatible',
              auto_applied: true,
              timestamp: Date.now(),
              source: METHOD_SIG,
            });
            isDev() &&
              console.log(
                `${LOG_PREFIX} Auto-apply requested for category ${categoryId} (variant: ${filterState.autoApplyVariantId})`
              );
          }

          // All-greyed: When no compatible variants exist, mark category as completed (filtered)
          if (
            filterState.allGreyedOut &&
            filterState.allVariantIds.length > 0
          ) {
            publishEvent(EVENTS.STATE_CATEGORY_COMPLETED, {
              categoryId: categoryId,
              completionType: 'material_filtered',
              reason: 'All options filtered out by material preference',
              timestamp: Date.now(),
              source: METHOD_SIG,
            });
            isDev() &&
              console.log(
                `${LOG_PREFIX} All-greyed category completion for ${categoryId} (${filterState.allVariantIds.length} variants all incompatible)`
              );
          }
        });

        // PATTERN 12 & 13: Performance tracking with thresholds
        const duration = performance.now() - startTime;
        trackPerformance('FILTER_STATE_COMPUTE', duration);
      } catch (error) {
        // PATTERN 14: Try/catch + FAILED events
        isDev() &&
          console.error(`${LOG_PREFIX} Error computing filter state`, error);

        publishEvent(EVENTS.MATERIAL_PREFERENCE_APPLY_FAILED, {
          error: error.message,
          operation: 'computeFilterState',
          timestamp: Date.now(),
          source: METHOD_SIG,
        });
      }
    },

    /**
     * Lazy sorted orders compute - called on first getSortedVariants() access per product
     *
     * Sorted orders were not pre-built during bootstrap (deferred to eliminate O(W*F) cost).
     * This method computes them on first access and caches in lazySortedOrdersCache.
     *
     * @param {String} productId - Product ID to compute orders for
     * @returns {Object|null} Frozen sorted orders structure, or null if unavailable
     * @private
     */
    _getLazySortedOrders: function (productId) {
      // Check lazy cache first (O(1) on subsequent calls for same product)
      if (lazySortedOrdersCache.has(productId)) {
        return lazySortedOrdersCache.get(productId);
      }

      // Delegate to conductor - CCP compliant (conductor owns Phase 1 <-> Phase 2 relationships)
      const conductor = window.MurphyDoorConfigurator.conductor;
      if (!conductor?.getLazySortedOrdersForProduct) {
        isDev() &&
          console.warn(
            `${LOG_PREFIX} conductor.getLazySortedOrdersForProduct not available`
          );
        return null;
      }

      isDev() &&
        console.log(
          `${LOG_PREFIX} Lazy computing sorted orders for ${productId} (first access)`
        );
      const sortedOrders = conductor.getLazySortedOrdersForProduct(productId);
      if (sortedOrders) {
        lazySortedOrdersCache.set(productId, sortedOrders);
      }
      return sortedOrders;
    },

    /**
     * Fallback: Runtime filter state computation for unexpected preference combinations
     *
     * This mirrors the original O(C*P*V) computation but is ONLY used when the
     * pre-computed map doesn't contain the requested preference key. In practice,
     * this handles edge cases like wood+finish combos where no single variant has
     * both SKUs, but the user applied them from different categories independently.
     *
     * @param {Object} preferences - Current material preferences
     * @param {Object} ppd - Perfect Product Dictionary
     * @returns {Map} categoryFilters - Map<categoryId, filterState>
     * @private
     */
    _computeFilterStateFallback: function (preferences, ppd) {
      const categoryFilters = new Map();

      if (!ppd.categories) return categoryFilters;

      // Use centralized utility for variant compatibility classification (SINGLE SOURCE OF TRUTH)
      const computeCompat =
        window.MurphyDoorConfigurator.utils.computeVariantCompatibility;

      ppd.categories.forEach((categoryData, categoryId) => {
        // MATERIAL SOURCE GUARD. The categoryMaterialAwareness test below was
        // added (22d41eab, 2026-03-03) to keep the master category out of this
        // loop, on the premise that the awareness map "only contains DEPENDENT
        // categories". That premise is false in source, not just in
        // measurement: md-config-support-material-filter-builder.js:795 calls
        // awarenessMap.set() unconditionally inside its loop, so an entry
        // exists for EVERY category and the existence test can never be
        // falsy. The guard has therefore never fired.
        //
        // Naming the source categories outright is the fix -- the same repair
        // 2f8cc833 already made to the sibling copy of this guard at
        // md-config-ui-updater.js:377. It cannot silently stop being true the
        // way the absence assumption did.
        //
        // What it was letting through, measured live 2026-08-19: with a wood
        // applied, this loop produced a filter-state entry for Wood & Finish
        // whose compatible set was exactly the one variant already applied.
        // Three of that entry's four consumers are unguarded --
        //   - autoApplyVariantId -> SELECTION_AUTO_APPLY_REQUEST (below),
        //     re-applying the customer's own wood over the top; only
        //     manualSelections suppressed it, and any unapply clears that.
        //   - allGreyedOut -> STATE_CATEGORY_COMPLETED.
        //   - bubbleUpVariants -> md-config-ui-updater.js:4443, which rewrote
        //     the applied wood's tile to data-bubbled with a pre-resolved
        //     variant id. A bubbled tile takes the direct-apply path at
        //     md-config-core-event-handler.js:1191 instead of opening the
        //     finish browse, so clicking your own wood re-applied the
        //     identical variant and its other stains were unreachable.
        // That last one is the customer-visible bug: "I can't change stains
        // without changing wood." It looked White-Oak-specific only because WO
        // has two finishes and is where you are most likely to try.
        //
        // Skipping at the top of the loop -- rather than at the three publish
        // sites -- is deliberate: the entry itself is what feeds bubble-up, so
        // guarding the publishes alone would leave the tile still bubbled.
        //
        // Additive on purpose: this can only skip MORE categories than before,
        // never fewer, so no category starts getting filter state it was not
        // already getting.
        const materialSourceCategories =
          CONSTANTS.MATERIAL_SOURCE_CATEGORIES || [];
        if (materialSourceCategories.indexOf(categoryId) !== -1) {
          return;
        }

        // Guard: Only compute filter state for categories that are DEPENDENT on material
        // preferences (listed in categoryMaterialAwareness). The master category (Wood &
        // Finish) is the SOURCE of preferences - computing filter state for it would
        // produce an entry that marks all non-selected woods as incompatible, which would
        // then be applied back to that same category, hiding all alternatives.
        if (!ppd.categoryMaterialAwareness?.get(categoryId)) {
          isDev() &&
            console.log(
              `${LOG_PREFIX} Skipping filter state for category ${categoryId} (not in categoryMaterialAwareness — likely master material category)`
            );
          return;
        }

        const categoryProducts = categoryData.products || [];
        const allVariantIds = [];
        const compatibleVariantIds = [];
        const incompatibleVariantIds = [];
        const bubbleUpVariants = new Map();

        categoryProducts.forEach((product) => {
          const productId = product.id;
          const allVariants = this._getAllVariantsForProduct(productId) || [];
          const filteredVariants =
            this.getFilteredVariants(productId, preferences) || [];

          // Centralized compatibility logic - no duplication
          const compat = computeCompat(allVariants, filteredVariants);
          allVariantIds.push(...compat.all);
          compatibleVariantIds.push(...compat.compatible);
          incompatibleVariantIds.push(...compat.incompatible);

          // BUBBLE-UP DETECTION (fallback path mirrors bootstrap logic)
          if (allVariants.length > 1 && compat.compatible.length === 1) {
            const isMaterialAware = allVariants.some(
              (v) => v.wood_sku || v.finish_sku
            );

            if (isMaterialAware) {
              const bubbledVariantId = compat.compatible[0];
              const bubbledVariant = allVariants.find(
                (v) => v.id === bubbledVariantId
              );

              if (bubbledVariant) {
                const productData = ppd.products
                  ? ppd.products.get(productId)
                  : null;
                const variantImageSrc =
                  bubbledVariant.featured_image &&
                  bubbledVariant.featured_image.src
                    ? bubbledVariant.featured_image.src
                    : productData && productData.image
                      ? typeof productData.image === 'string'
                        ? productData.image
                        : productData.image.src
                      : null;
                const originalProductImageSrc =
                  productData && productData.image
                    ? typeof productData.image === 'string'
                      ? productData.image
                      : productData.image.src
                    : null;

                bubbleUpVariants.set(
                  productId,
                  Object.freeze({
                    variantId: bubbledVariantId,
                    variantImageSrc: variantImageSrc,
                    variantPrice: bubbledVariant.price || null,
                    productTitle: productData
                      ? productData.title || productData.display_name || ''
                      : '',
                    sourceProductId: productId,
                    productType: productData ? productData.type : null,
                    originalProductImageSrc: originalProductImageSrc,
                    woodSku: bubbledVariant.wood_sku || null,
                    finishSku: bubbledVariant.finish_sku || null,
                  })
                );
              }
            }
          }
        });

        const allGreyedOut = compatibleVariantIds.length === 0;
        const autoApplyVariantId =
          compatibleVariantIds.length === 1 ? compatibleVariantIds[0] : null;

        categoryFilters.set(
          categoryId,
          Object.freeze({
            compatibleVariantIds: Object.freeze(compatibleVariantIds),
            incompatibleVariantIds: Object.freeze(incompatibleVariantIds),
            allVariantIds: Object.freeze(allVariantIds),
            autoApplyVariantId,
            allGreyedOut,
            bubbleUpVariants: Object.freeze(
              bubbleUpVariants.size > 0 ? bubbleUpVariants : null
            ),
          })
        );
      });

      return categoryFilters;
    },

    /**
     * PATTERN 15: Cleanup method for resource management
     * Called during system shutdown to unsubscribe all event handlers
     */
    cleanup: function () {
      isDev() && console.log(`${LOG_PREFIX} Cleaning up resources`);

      cleanupHandlers.forEach((unsubscribe) => {
        try {
          unsubscribe();
        } catch (e) {
          /* ignore */
        }
      });
      cleanupHandlers.length = 0;

      // Clear lazy caches
      lazySortedOrdersCache.clear();
      lazyFilterStateCache.clear();

      isInitialized = false;
      bootstrapComplete = false;

      publishEvent(EVENTS.MODULE_MATERIAL_PREFERENCE_COORDINATOR_CLEANUP, {
        module: MODULE_NAME,
        timestamp: Date.now(),
      });
    },
  };

  // ============================================================================
  // PHASE 2 INITIALIZATION: Centralized conductor dependency validation + event subscriptions
  //
  // All conductor dependency checks are performed ONCE here at initialization time.
  // This eliminates redundant runtime checks in applyMaterialPreference, clearMaterialPreference,
  // and computeAndPublishFilterState. If any required conductor method is missing, we fail
  // fast here rather than silently degrading at runtime.
  // ============================================================================
  if (CoreEventBus) {
    // Subscribe to system:maps_initialized event (waits for conductor's async map initialization)
    const unsubMapsInit = CoreEventBus.subscribe(
      EVENTS.SYSTEM_MAPS_INITIALIZED,
      function () {
        // PATTERN 16: Initialization guard
        if (isInitialized) {
          isDev() &&
            console.warn(`${LOG_PREFIX} Already initialized, skipping`);
          return;
        }

        // Centralized conductor dependency validation (fail-fast at init, not at runtime)
        const conductor = window.MurphyDoorConfigurator.conductor;
        const coordinator =
          window.MurphyDoorConfigurator.materialPreferenceCoordinator;
        const requiredMethods = [
          'getMaterialPreferences',
          'updateMaterialPreferencesFromObject',
          'updateMaterialFilterState',
          'getPerfectProductDictionary',
          'getLazySortedOrdersForProduct',
          'getAppliedSelectionsMap',
        ];

        const missingMethods = requiredMethods.filter(function (method) {
          return !conductor || typeof conductor[method] !== 'function';
        });

        if (missingMethods.length > 0) {
          isDev() &&
            console.error(
              `${LOG_PREFIX} CRITICAL - Conductor missing required delegation methods: ` +
                missingMethods.join(', ') +
                '. Module will not function correctly.'
            );
          return;
        }

        // Track which categoryId contributed which material SKUs and when.
        // Enables surviving-contributor fallback when a category is unapplied:
        // if other categories still have the same material type, the most recently applied one wins.
        // Map<categoryId, { woodSku: string|null, finishSku: string|null, lastAppliedTimestamp: number }>
        const categoryMaterialSourceMap = new Map();

        // Cascade unapply in-progress flag - suppresses surviving-contributor logic
        // during cascade to prevent intermediate state flashes
        let cascadeUnapplyInProgress = false;

        // PATTERN 4: Event consumer handler - selection:variant_applied
        const unsubVariantApplied = CoreEventBus.subscribe(
          EVENTS.SELECTION_VARIANT_APPLIED,
          function (eventData) {
            try {
              const { variant, categoryId, productId } = eventData;
              const timestamp = Date.now();

              if (!variant) {
                isDev() &&
                  console.warn(
                    `${LOG_PREFIX} No variant in selection:variant_applied event`
                  );
                return;
              }

              // Build context for material preference
              const context = {
                variantId: variant.id,
                categoryId,
                productId,
                timestamp: timestamp,
              };

              // Determine if this category is a "material master" category.
              // CompositeProduct variants (isCompositeProductVariant = true) are content products
              // with derived material properties - they are NOT material master categories.
              // Only standalone material-selector products (e.g. "Wood & Finish") can be masters.
              const isMaterialMasterCategory =
                !variant.isCompositeProductVariant;

              // Check if variant has wood_sku and apply wood preference
              if (variant.wood_sku) {
                isDev() &&
                  console.log(
                    `${LOG_PREFIX} Detected wood selection (${variant.wood_sku}) isMaster=${isMaterialMasterCategory}`
                  );
                // Update source map BEFORE applying preference (defensive ordering)
                const existingEntry = categoryMaterialSourceMap.get(
                  categoryId
                ) || {
                  woodSku: null,
                  finishSku: null,
                  lastAppliedTimestamp: 0,
                  isMaterialMasterCategory: false,
                };
                categoryMaterialSourceMap.set(categoryId, {
                  woodSku: variant.wood_sku,
                  finishSku: existingEntry.finishSku,
                  lastAppliedTimestamp: timestamp,
                  isMaterialMasterCategory:
                    isMaterialMasterCategory ||
                    existingEntry.isMaterialMasterCategory,
                });
                coordinator.applyMaterialPreference(
                  CONSTANTS.MATERIAL_TYPES.WOOD,
                  variant.wood_sku,
                  context
                );
              }

              // Check if variant has finish_sku and apply finish preference
              if (variant.finish_sku) {
                isDev() &&
                  console.log(
                    `${LOG_PREFIX} Detected finish selection (${variant.finish_sku}) isMaster=${isMaterialMasterCategory}`
                  );
                // Update source map BEFORE applying preference (defensive ordering)
                const existingEntryForFinish = categoryMaterialSourceMap.get(
                  categoryId
                ) || {
                  woodSku: null,
                  finishSku: null,
                  lastAppliedTimestamp: 0,
                  isMaterialMasterCategory: false,
                };
                categoryMaterialSourceMap.set(categoryId, {
                  woodSku: existingEntryForFinish.woodSku,
                  finishSku: variant.finish_sku,
                  lastAppliedTimestamp: timestamp,
                  isMaterialMasterCategory:
                    isMaterialMasterCategory ||
                    existingEntryForFinish.isMaterialMasterCategory,
                });
                coordinator.applyMaterialPreference(
                  CONSTANTS.MATERIAL_TYPES.FINISH,
                  variant.finish_sku,
                  context
                );
              }
            } catch (error) {
              isDev() &&
                console.error(
                  `${LOG_PREFIX} Error handling variant_applied event`,
                  error
                );

              // PATTERN 14: Failed event on error
              publishEvent(EVENTS.MATERIAL_PREFERENCE_APPLY_FAILED, {
                error: error.message,
                operation: 'variant_applied_handler',
                timestamp: Date.now(),
                source: `${MODULE_NAME}.variantAppliedHandler`,
              });
            }
          }
        );
        cleanupHandlers.push(unsubVariantApplied);

        // PATTERN 4: Event consumer handler - selection:user_unapply_completed
        // Handle material preference updates when a category is unapplied.
        // When user clicks x to unapply a category:
        // - If the category is a "dual-material authority" (contributed BOTH wood AND finish),
        //   cascade-unapply ALL other categories whose wood_sku or finish_sku matches
        // - If other applied categories still contribute the same material type,
        //   the most recently applied surviving contributor wins (preference SWITCHES, not clears)
        // - If no other applied categories contribute that material type,
        //   the preference is CLEARED
        // All preference changes are made atomically to avoid intermediate state issues.
        const unsubUnapply = CoreEventBus.subscribe(
          EVENTS.SELECTION_USER_UNAPPLY_COMPLETED,
          function (eventData) {
            try {
              const { categoryId } = eventData || {};
              if (!categoryId) return;

              const removedEntry = categoryMaterialSourceMap.get(categoryId);
              if (!removedEntry) return; // Natural loop terminator for cascade targets

              isDev() &&
                console.log(
                  `${LOG_PREFIX} Processing unapply for category "${categoryId}"`,
                  { removedEntry: removedEntry }
                );

              // ====================================================================
              // CASCADE UNAPPLY: Dual-Material-Authority Guard
              // If this category contributed BOTH wood AND finish SKUs AND is a
              // "material master" category (e.g., Wood & Finish standalone products),
              // unapplying it should cascade to all other applied categories with
              // matching SKUs.
              //
              // isMaterialMasterCategory = true  -> standalone material-selector product
              //                                    (e.g. "White Oak Veneer" in Wood & Finish)
              // isMaterialMasterCategory = false -> CompositeProduct content variant
              //                                    (e.g. "Crown Front Entablature" -- has
              //                                     wood/finish props but is NOT the master)
              // ====================================================================
              const isDualMaterialAuthority =
                removedEntry.woodSku !== null &&
                removedEntry.finishSku !== null &&
                removedEntry.isMaterialMasterCategory === true;

              let cascadeTargets = [];
              if (isDualMaterialAuthority) {
                // Identify all cascade targets BEFORE any deletes (snapshot of matching categories)
                categoryMaterialSourceMap.forEach(function (entry, catId) {
                  if (catId === categoryId) return; // Skip self
                  const woodMatches =
                    removedEntry.woodSku &&
                    entry.woodSku === removedEntry.woodSku;
                  const finishMatches =
                    removedEntry.finishSku &&
                    entry.finishSku === removedEntry.finishSku;
                  if (woodMatches || finishMatches) {
                    cascadeTargets.push(catId);
                  }
                });

                if (cascadeTargets.length > 0) {
                  isDev() &&
                    console.log(
                      `${LOG_PREFIX} Cascade unapply triggered for ${cascadeTargets.length} dependent categories`,
                      {
                        cascadeTargets: cascadeTargets,
                        removedEntry: removedEntry,
                      }
                    );

                  // ATOMIC PRE-DELETE: Remove ALL cascade targets from the map BEFORE any events fire.
                  // This ensures cascade targets hit the early-exit guard when their
                  // SELECTION_USER_UNAPPLY_COMPLETED fires, preventing infinite loops.
                  cascadeTargets.forEach(function (catId) {
                    categoryMaterialSourceMap.delete(catId);
                  });

                  // Set flag to suppress surviving-contributor logic during cascade
                  cascadeUnapplyInProgress = true;
                }
              }

              // Delete the unapplied category from the map
              categoryMaterialSourceMap.delete(categoryId);

              // ====================================================================
              // SURVIVING-CONTRIBUTOR SCAN
              // Suppressed during cascade (cascadeUnapplyInProgress = true) because:
              // 1. We're fully clearing all material preferences
              // 2. Surviving-contributor would incorrectly SWITCH preference to a
              //    cascade target that is about to be unapplied, causing state flash
              // ====================================================================
              let survivingWoodSku = null;
              let survivingWoodTimestamp = 0;
              let survivingFinishSku = null;
              let survivingFinishTimestamp = 0;

              if (!cascadeUnapplyInProgress) {
                // Normal (non-cascade) path: find surviving contributors
                categoryMaterialSourceMap.forEach(function (entry) {
                  if (
                    entry.woodSku &&
                    entry.lastAppliedTimestamp > survivingWoodTimestamp
                  ) {
                    survivingWoodSku = entry.woodSku;
                    survivingWoodTimestamp = entry.lastAppliedTimestamp;
                  }
                  if (
                    entry.finishSku &&
                    entry.lastAppliedTimestamp > survivingFinishTimestamp
                  ) {
                    survivingFinishSku = entry.finishSku;
                    survivingFinishTimestamp = entry.lastAppliedTimestamp;
                  }
                });
              }
              // During cascade: survivingWoodSku and survivingFinishSku remain null -> full clear

              const currentPrefs = conductor.getMaterialPreferences() || {
                woodApplied: false,
                finishApplied: false,
                appliedWoodSku: null,
                appliedFinishSku: null,
                lastUpdated: null,
              };

              const now = Date.now();
              const updatedPrefs = {
                ...currentPrefs,
                lastUpdated: now,
                woodApplied: survivingWoodSku !== null,
                appliedWoodSku: survivingWoodSku,
                finishApplied: survivingFinishSku !== null,
                appliedFinishSku: survivingFinishSku,
              };

              // Atomic state update
              conductor.updateMaterialPreferencesFromObject(updatedPrefs);

              // Determine what actually changed for targeted events
              const woodWasCleared = removedEntry.woodSku && !survivingWoodSku;
              const finishWasCleared =
                removedEntry.finishSku && !survivingFinishSku;
              const woodSwitched =
                removedEntry.woodSku &&
                survivingWoodSku &&
                removedEntry.woodSku !== survivingWoodSku;
              const finishSwitched =
                removedEntry.finishSku &&
                survivingFinishSku &&
                removedEntry.finishSku !== survivingFinishSku;

              // Publish CLEARED events for material types with no survivor
              // NOTE: cascade: true in payload allows stale indicator to suppress marking
              // during cascade (categories being cascade-unapplied don't need stale markers)
              if (woodWasCleared) {
                publishEvent(EVENTS.MATERIAL_PREFERENCE_CLEARED, {
                  materialType: CONSTANTS.MATERIAL_TYPES.WOOD,
                  preferences: updatedPrefs,
                  cascade: cascadeUnapplyInProgress,
                  unappliedCategoryId: categoryId,
                  timestamp: now,
                  source: `${MODULE_NAME}.unapplyHandler`,
                });
              }
              if (finishWasCleared) {
                publishEvent(EVENTS.MATERIAL_PREFERENCE_CLEARED, {
                  materialType: CONSTANTS.MATERIAL_TYPES.FINISH,
                  preferences: updatedPrefs,
                  cascade: cascadeUnapplyInProgress,
                  unappliedCategoryId: categoryId,
                  timestamp: now,
                  source: `${MODULE_NAME}.unapplyHandler`,
                });
              }

              // Publish RECOMPUTED event when preference SWITCHED to a surviving contributor
              if (woodSwitched || finishSwitched) {
                publishEvent(
                  EVENTS.MATERIAL_PREFERENCES_RECOMPUTED_FROM_APPLIED,
                  {
                    survivingWoodSku: survivingWoodSku,
                    survivingFinishSku: survivingFinishSku,
                    previousWoodSku: removedEntry.woodSku,
                    previousFinishSku: removedEntry.finishSku,
                    preferences: updatedPrefs,
                    cascade: cascadeUnapplyInProgress,
                    unappliedCategoryId: categoryId,
                    timestamp: now,
                    source: `${MODULE_NAME}.unapplyHandler`,
                  }
                );
                isDev() &&
                  console.log(
                    `${LOG_PREFIX} Material preference switched to surviving contributor`,
                    {
                      wood: woodSwitched
                        ? removedEntry.woodSku + ' -> ' + survivingWoodSku
                        : 'unchanged',
                      finish: finishSwitched
                        ? removedEntry.finishSku + ' -> ' + survivingFinishSku
                        : 'unchanged',
                    }
                  );
              }

              // ====================================================================
              // CASCADE PUBLISH: Request unapply for each dependent category
              // CRITICAL SYNCHRONOUS EVENT BUS DEPENDENCY:
              // The loop prevention mechanism (atomic pre-delete above) ONLY works
              // because CoreEventBus.publish() is fully synchronous (depth-first).
              // Each cascade target's SELECTION_USER_UNAPPLY_COMPLETED handler runs
              // to completion before the next publish. If the event bus is ever made
              // async, this cascade logic MUST be refactored to prevent infinite loops.
              // ====================================================================
              if (cascadeTargets.length > 0) {
                cascadeTargets.forEach(function (catId) {
                  isDev() &&
                    console.log(`${LOG_PREFIX} Cascade unapply -> "${catId}"`);
                  publishEvent(EVENTS.SELECTION_USER_UNAPPLY_REQUESTED, {
                    categoryId: catId,
                    cascade: true,
                    cascadeReason: 'material_authority_unapply',
                    cascadeSourceCategoryId: categoryId,
                    source: `${MODULE_NAME}.cascadeUnapply`,
                  });
                });
              }

              // Single filter state computation AFTER all events (including cascades) complete
              coordinator.computeAndPublishFilterState();

              // Reset cascade flag after all work is done
              cascadeUnapplyInProgress = false;

              isDev() &&
                console.log(
                  `${LOG_PREFIX} Unapply complete for category "${categoryId}"`,
                  {
                    survivingWood: survivingWoodSku,
                    survivingFinish: survivingFinishSku,
                    woodWasCleared: woodWasCleared,
                    finishWasCleared: finishWasCleared,
                    woodSwitched: woodSwitched,
                    finishSwitched: finishSwitched,
                    cascadeTargets: cascadeTargets,
                  }
                );
            } catch (error) {
              cascadeUnapplyInProgress = false; // Always reset on error

              // PATTERN 14: Failed event on error
              publishEvent(EVENTS.MATERIAL_PREFERENCE_UNAPPLY_FAILED, {
                error: error.message,
                categoryId: eventData?.categoryId,
                timestamp: Date.now(),
                source: `${MODULE_NAME}.unapplyHandler`,
              });

              isDev() &&
                console.error(`${LOG_PREFIX} Error processing unapply`, error);
            }
          }
        );
        cleanupHandlers.push(unsubUnapply);

        // Keep categoryMaterialSourceMap synchronized when preferences are cleared
        // from ANY source (not just unapply). If all preferences are gone, the map is stale
        // and must be reset to avoid incorrect behavior on subsequent unapplies.
        const unsubPrefCleared = CoreEventBus.subscribe(
          EVENTS.MATERIAL_PREFERENCE_CLEARED,
          function (eventData) {
            const prefs = eventData.preferences || {};
            if (!prefs.woodApplied && !prefs.finishApplied) {
              if (categoryMaterialSourceMap.size > 0) {
                categoryMaterialSourceMap.clear();
                isDev() &&
                  console.log(
                    `${LOG_PREFIX} All material preferences cleared, resetting category source map.`
                  );
              }
            }
          }
        );
        cleanupHandlers.push(unsubPrefCleared);

        // Mark initialized
        isInitialized = true;

        // PATTERN 20: Conductor single publication - module ready
        publishEvent(EVENTS.MODULE_MATERIAL_PREFERENCE_COORDINATOR_READY, {
          module: MODULE_NAME,
          timestamp: Date.now(),
        });

        isDev() &&
          console.log(
            `${LOG_PREFIX} Conductor dependencies validated, initialized and ready for runtime`
          );
      }
    );
    cleanupHandlers.push(unsubMapsInit);
  }
})(window);
